// Shared TanStack Query client.
//
// One client is created at module-load time and consumed by both the
// <QueryClientProvider> wrapper in src/main.tsx AND any place that needs
// to imperatively invalidate / mutate the cache (e.g. mutations inside
// TodayScreen). Importing the singleton — rather than reading it from
// `useQueryClient()` — keeps invalidations consistent for non-hook code
// paths such as the offline-queue drain callback.
//
// Defaults follow the TanStack Query v5 docs:
//   https://tanstack.com/query/v5/docs/react/guides/important-defaults
//
// Tuning rationale for this app:
//   - `staleTime: 60_000`  — most read endpoints (doses, taper, streak,
//     pledge, digest, handoff, letters, etc.) tolerate ~1 min of
//     freshness. The Today screen mounts ~15 queries; a 60s stale
//     window means a quick back-navigation hits cache instead of
//     re-firing each one.
//   - `gcTime: 5 * 60_000`  — keep retired queries around for 5 min so
//     a tab-switch or quick re-route still serves cached data while a
//     background refetch happens.
//   - `refetchOnWindowFocus: true`  — recovery-context app; when the
//     user comes back to the tab we want fresh streak / dose data
//     without a manual pull-to-refresh.
//   - `retry: 1`  — the existing `apiGet` already handles auth refresh
//     and one bounce; we don't want the query layer to triple every
//     error into 4 round-trips.

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});
